import React, { useState, useEffect } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Pagination
} from "@mui/material";
import { IoEyeOutline, IoTrashOutline } from 'react-icons/io5';
import { postData, getData, putData } from "../../Webservice/Webservice";

function AppPolicy() {
  const [view, setView] = useState('list'); // 'list', 'form', or 'view'
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false); // Track edit mode
  const [formData, setFormData] = useState({
    policyType: "",
    title: "",
    contentHtml: "",
    effectiveDate: "",
    summary: "",
    makeActive: false
  });
  const [policyTypeFilter, setPolicyTypeFilter] = useState("");
  
  // Pagination state
  const PAGE_SIZE = 10;
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });

  // Pagination handler
  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  // Fetch policies from backend
  const fetchPolicies = async (page = 1) => {
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      let url = `policy/list?`;
      if (policyTypeFilter) {
        url += `&policyType=${policyTypeFilter}`;
      }
      // Add pagination parameters
      url += `&page=${page}&limit=${PAGE_SIZE}`;
      
      const res = await getData(url, null, token);
      setPolicies(res.policies || []);
      
      // Update pagination meta
      const meta = res?.meta || { page: 1, totalPages: 1, total: 0 };
      setMeta({
        page: meta.page,
        totalPages: meta.totalPages,
        total: meta.total
      });
    } catch (err) {
      setPolicies([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
    }
  };

  useEffect(() => {
    if (view === 'list') {
      fetchPolicies(meta.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyTypeFilter, view, meta.page]);

  const handleOpenForm = () => {
    setIsEditMode(false); // Ensure not in edit mode for new policy
    setView('form');
  };

  const handleEditPolicy = (policy) => {
    setSelectedPolicy(policy);
    setFormData({
      policyType: policy.policyType || "",
      title: policy.title || "",
      contentHtml: policy.contentHtml || "",
      effectiveDate: policy.effectiveDate ? new Date(policy.effectiveDate).toISOString().split('T')[0] : "",
      summary: policy.summary || "",
      makeActive: policy.isActive || false
    });
    setIsEditMode(true); // Set edit mode
    setView('form');
  };

  const handleViewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setView('view');
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm("Are you sure you want to delete this policy?")) return;
    try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        await postData(`policy/delete/${policyId}`, {}, token);
        alert("Policy deleted successfully");
        if (view === 'view') {
            handleBackToList();
        } else {
            // Refresh current list page
            fetchPolicies(meta.page);
        }
    } catch (error) {
        alert("Failed to delete policy. Please try again.");
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedPolicy(null);
    setIsEditMode(false); // Reset edit mode
    setFormData({
      policyType: "",
      title: "",
      contentHtml: "",
      effectiveDate: "",
      summary: "",
      makeActive: false
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleEditorChange = (value) => {
    setFormData(prev => ({
      ...prev,
      contentHtml: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      if (isEditMode) {
        await postData(`policy/update/${selectedPolicy._id || selectedPolicy.id}`, formData, token);
        alert(`${formData.policyType.toUpperCase()} Policy updated successfully`);
      } else {
        await postData("policy/add", formData, token);
        alert(`${formData.policyType.toUpperCase()} Policy added successfully`);
      }
      handleBackToList();
    } catch (error) {
      alert(`Failed to ${isEditMode ? 'update' : 'add'} policy. Please try again.`);
    }
  };

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  // Quill editor formats
  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'link', 'image', 'align'
  ];

  const policyTypes = [
    { value: "privacy", label: "Privacy" },
    { value: "terms", label: "Terms & Conditions" },
    { value: "return_refund", label: "Return & Refund Policy" },
    { value: "shipping", label: "Shipping Policy" },
    { value: "payment", label: "Payment Policy" },
    { value: "cancellation", label: "Cancellation Policy" },
    { value: "useraccount", label: "User Policy" },
    { value: "liability_limitation", label: "Liability Limitation" }
  ];

  const getPolicyTypeColor = (type) => {
    const colors = {
      privacy: "primary",
      terms: "secondary",
      return_refund: "success",
      shipping: "info",
      payment: "success",
      cancellation: "warning",
      useraccount: "default",
      liability_limitation: "error"
    };
    return colors[type] || "default";
  };

  // Render the view policy details
  if (view === 'view') {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          maxWidth: 1200,
          margin: "0 auto",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          position: "relative"
        }}
      >
        {/* Policy Details */}
        <Box
          sx={{
            bgcolor: 'white',
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            maxWidth: 800,
            mx: 'auto',
            transition: 'box-shadow 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
          }}
        >
          {/* Header Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  fontFamily: '"Roboto", sans-serif',
                  color: '#1f2937',
                }}
              >
                Policy Details
              </Typography>
              <Chip
                label={policyTypes.find((t) => t.value === selectedPolicy?.policyType)?.label || selectedPolicy?.policyType || 'Unknown'}
                color={getPolicyTypeColor(selectedPolicy?.policyType)}
                variant="outlined"
                size="small"
                sx={{
                  fontWeight: 500,
                  fontFamily: '"Roboto", sans-serif',
                  bgcolor: 'grey.50',
                  borderColor: 'grey.300',
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => handleEditPolicy(selectedPolicy)}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontFamily: '"Roboto", sans-serif',
                  color: '#2563eb',
                  borderColor: '#2563eb',
                  px: 2,
                  py: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: '#eff6ff',
                    borderColor: '#2563eb',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleDeletePolicy(selectedPolicy?._id || selectedPolicy?.id)}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontFamily: '"Roboto", sans-serif',
                  color: '#dc2626',
                  borderColor: '#dc2626',
                  px: 2,
                  py: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: '#fef2f2',
                    borderColor: '#dc2626',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Delete
              </Button>
              <Button
                variant="outlined"
                onClick={handleBackToList}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontFamily: '"Roboto", sans-serif',
                  color: '#dc2626',
                  borderColor: '#dc2626',
                  px: 2,
                  py: 0.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: '#fef2f2',
                    borderColor: '#dc2626',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </Box>

          {/* Details Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              bgcolor: '#f9fafb',
              mb: 3,
              border: '1px solid #e5e7eb',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Title */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    minWidth: { md: 120 },
                    color: '#1f2937',
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  Title
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    flexGrow: 1,
                    color: '#4b5563',
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  {selectedPolicy?.title || 'Not specified'}
                </Typography>
              </Box>

              {/* Effective Date */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    minWidth: { md: 120 },
                    color: '#1f2937',
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  Effective Date
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#4b5563',
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  {selectedPolicy?.effectiveDate
                    ? new Date(selectedPolicy.effectiveDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Not specified'}
                </Typography>
              </Box>

              {/* Summary */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    minWidth: { md: 120 },
                    color: '#1f2937',
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  Summary
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    flexGrow: 1,
                    color: '#4b5563',
                    fontFamily: '"Roboto", sans-serif',
                    lineHeight: 1.5,
                  }}
                >
                  {selectedPolicy?.summary || 'No summary available.'}
                </Typography>
              </Box>

              {/* Status */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    minWidth: { md: 120 },
                    color: '#1f2937',
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={selectedPolicy?.isActive ? 'Active' : 'Inactive'}
                  color={selectedPolicy?.isActive ? 'success' : 'default'}
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontFamily: '"Roboto", sans-serif',
                    borderColor: selectedPolicy?.isActive ? '#22c55e' : '#d1d5db',
                  }}
                />
              </Box>
            </Box>
          </Paper>

          {/* Content Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#1f2937',
                fontFamily: '"Roboto", sans-serif',
                mb: 1.5,
              }}
            >
              Content
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  fontFamily: '"Roboto", sans-serif',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: '#374151',
                }}
                dangerouslySetInnerHTML={{
                  __html: selectedPolicy?.contentHtml || '<p>No content available.</p>',
                }}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  // Render the form view
  if (view === 'form') {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          maxWidth: 1200,
          margin: "0 auto",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          position: "relative"
        }}
      >
        {/* Policy Form */}
        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: 3,
            bgcolor: "background.paper",
            p: 3,
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          {/* Back Button and Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Mulish, sans-serif' }}>
              {isEditMode ? 'Edit Policy' : 'Add New Policy'}
            </Typography>
            <Button
              variant="contained"
              onClick={handleBackToList}
              sx={{
                color: 'white',
                background: 'red',
                '&:hover': { bgcolor: 'red' },
              }}
            >
              Back
            </Button>
          </Box>
          
          <TextField
            select
            label="Policy Type"
            name="policyType"
            value={formData.policyType}
            onChange={handleChange}
            required
            fullWidth
          >
            {policyTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            fullWidth
          />

          <TextField
            label="Effective Date"
            name="effectiveDate"
            type="date"
            value={formData.effectiveDate}
            onChange={handleChange}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Summary"
            name="summary"
            value={formData.summary}
            onChange={handleChange}
            required
            fullWidth
            multiline
            rows={2}
          />

          <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
            Content (HTML)
          </Typography>
          <ReactQuill
            value={formData.contentHtml}
            onChange={handleEditorChange}
            modules={modules}
            formats={formats}
            theme="snow"
            style={{ 
              height: '200px', 
              marginBottom: '40px' 
            }}
          />

          <FormControlLabel
            control={
              <Switch
                name="makeActive"
                checked={formData.makeActive}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Make this policy active immediately"
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button onClick={handleBackToList} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ 
              backgroundColor: "rgb(255, 209, 2)",
              color: "black",
              "&:hover": {
                backgroundColor: "rgb(230, 189, 0)"
              }
            }}>
              {isEditMode ? 'Update Policy' : 'Add Policy'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // Render the list view (default)
  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        margin: "0 auto",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        position: "relative"
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "#1a202c",
              fontFamily: "Mulish, sans-serif"
            }}
          >
            App Policy
          </Typography>
          <Chip
            label={`${meta.total || policies.length} ${meta.total === 1 ? 'Item' : 'Items'}`}
            size="small"
            sx={{ 
                backgroundColor: 'rgba(255, 209, 2, 0.2)', 
                color: '#b39200',
                fontFamily: 'Mulish, sans-serif', 
                fontWeight: '800',
                border: '1px solid rgba(255, 209, 2, 0.5)'
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleOpenForm}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              background: 'rgb(255 209 2)',
              color: 'black',
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '0.9rem',
              px: 3,
              boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
              border: '1px solid black',
              transition: 'all 0.2s ease',
              '&:hover': {
                  background: 'rgb(255 209 2)',
                  boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                  transform: 'translateY(-1px)'
              }
            }}
          >
            Add Policy
          </Button>
        </Box>
      </Box>

      {/* Policy Type Filter */}
      <Box sx={{ maxWidth: 300, mb: 1 }}>
        <TextField
          select
          label="Filter by Policy Type"
          value={policyTypeFilter}
          variant="outlined"
          size="small"
          sx={{ 
            width: { xs: '100%', sm: '320px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontFamily: 'Mulish, sans-serif',
              backgroundColor: 'white'
            }
          }}
          onChange={e => setPolicyTypeFilter(e.target.value)}
          fullWidth
        >
          <MenuItem value="" sx={{ fontFamily: 'Mulish, sans-serif' }}>All Types</MenuItem>
          {policyTypes.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontFamily: 'Mulish, sans-serif' }}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Policies Table */}
      <TableContainer component={Paper} elevation={0} sx={{ 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
        borderRadius: '8px',
        border: '1px solid #d3d3d3de'
      }}>
        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="policies table">
          <TableHead>
            <TableRow sx={{ 
              '& th': {
                backgroundColor: '#f8f9fa',
                fontWeight: 800,
                py: 2,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: '#5c636a',
                fontFamily: 'Mulish, sans-serif',
                borderBottom: '2px solid #edeff1',
              }
            }}>
              <TableCell>Policy Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.length > 0 ? (
              policies.map((policy) => (
                <TableRow 
                  key={policy._id || policy.id} 
                  hover
                  onClick={() => handleEditPolicy(policy)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f9fafb' },
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell>
                    <Chip 
                      label={policyTypes.find(t => t.value === policy.policyType)?.label || policy.policyType} 
                      size="small"
                      sx={{ 
                        fontFamily:'Mulish, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        textTransform: 'uppercase',
                        borderRadius: '6px',
                        backgroundColor: policy.isActive ? '#def7ec' : '#f3f4f6',
                        color: policy.isActive ? '#03543f' : '#374151',
                        border: 'none'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      fontWeight={700} 
                      sx={{ 
                        fontFamily:'Mulish, sans-serif',
                        fontSize: '0.9rem',
                        color: '#1a1c1e'
                      }}
                    >
                      {policy.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'Mulish, sans-serif', color: '#4b5563' }}>
                      {policy.effectiveDate ? new Date(policy.effectiveDate).toLocaleDateString() : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'Mulish, sans-serif', 
                        color: '#6b7280',
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {policy.summary}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={policy.isActive ? "Active" : "Inactive"} 
                      size="small"
                      sx={{ 
                        fontFamily:'Mulish, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        textTransform: 'uppercase',
                        borderRadius: '6px',
                        backgroundColor: policy.isActive ? '#e1f5fe' : '#f5f5f5',
                        color: policy.isActive ? '#01579b' : '#616161',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small"
                      sx={{ 
                        color: '#4b5563',
                        backgroundColor: 'rgba(75, 85, 99, 0.1)',
                        '&:hover': { 
                          backgroundColor: 'rgba(255, 209, 2, 0.2)',
                          color: '#b39200'
                        },
                        borderRadius: '8px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewPolicy(policy);
                      }}
                      aria-label="view policy"
                    >
                      <IoEyeOutline size={20} />
                    </IconButton>
                    <IconButton 
                      size="small"
                      sx={{ 
                        color: '#dc2626',
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                        '&:hover': { 
                          backgroundColor: 'rgba(220, 38, 38, 0.2)',
                          color: '#b91c1c'
                        },
                        ml: 1,
                        borderRadius: '8px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePolicy(policy._id || policy.id);
                      }}
                      aria-label="delete policy"
                    >
                      <IoTrashOutline size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <img 
                      src="./img/nodata.png" 
                      alt="No policies" 
                      style={{ width: '180px' }} 
                    />  
                    <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif', color: 'text.secondary' }}>
                      No policies added yet.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Component */}
      {policies.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
          <Pagination
            count={meta.totalPages}
            page={meta.page}
            onChange={handlePageChange}
            sx={{
              '& .MuiPaginationItem-root': {
                  fontFamily: 'Mulish, sans-serif',
                  fontWeight: 600,
                  borderRadius: '8px',
                  '&.Mui-selected': {
                      backgroundColor: 'rgb(255, 209, 2)',
                      color: 'black',
                      '&:hover': {
                          backgroundColor: 'rgb(255, 190, 0)',
                      }
                  }
              }
            }}
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Pagination Info */}
      {meta.total > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
        </Typography>
      )}
    </Box>
  );
}

export default AppPolicy;